import { useState, useEffect, useContext, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Search as SearchIcon, Plus, User, Moon, Sun, Bot, X, TrendingUp, Map as MapIcon, Eye, Clock, Bell, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useTheme } from 'next-themes';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const AskVexiChat = lazy(() => import('@/components/AskVexiChat'));

export default function Search() {
  const navigate = useNavigate();
  const { selectedCity, token, user, selectCity, userLocation } = useContext(AuthContext);
  const { theme, setTheme } = useTheme();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [trending, setTrending] = useState([]);
  const [showTrending, setShowTrending] = useState(false);
  const [topProducts, setTopProducts] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [openRequests, setOpenRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestProduct, setRequestProduct] = useState('');
  const [requestDetails, setRequestDetails] = useState('');
  const [creatingRequest, setCreatingRequest] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [footerLiftPx, setFooterLiftPx] = useState(24);

  useEffect(() => {
    // Lift floating actions when the footer approaches the viewport.
    const footer = document.querySelector('footer');
    if (!footer) return;

    const BASE_PX = 24; // matches tailwind bottom-6
    const PAD_PX = 12;

    let raf = 0;
    const compute = () => {
      raf = 0;
      const rect = footer.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      // How many px of footer are visible in the viewport
      const visible = Math.max(0, vh - rect.top);
      // Keep a stable baseline until the footer is actually visible.
      const lift =
        visible <= 0 ? BASE_PX : Math.min(260, Math.round(BASE_PX + visible + PAD_PX));
      setFooterLiftPx(lift);
    };

    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    // Also observe the footer so we still update reliably if scrolling happens in a container.
    let obs;
    if ('IntersectionObserver' in window) {
      obs = new IntersectionObserver(() => schedule(), {
        root: null,
        threshold: 0.0,
        rootMargin: '200px 0px 200px 0px',
      });
      obs.observe(footer);
    }

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (obs) obs.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!selectedCity) {
      navigate('/');
    }
  }, [selectedCity, navigate]);

  useEffect(() => {
    // Fetch trending products
    const fetchTrending = async () => {
      try {
        const isMobile = window.matchMedia('(max-width: 640px)').matches;
        const limit = isMobile ? 5 : 10;
        const params = new URLSearchParams({ limit: String(limit), days: '7' });
        const response = await fetch(`${API}/trending?${params.toString()}`);
        const data = await response.json();
        setTrending(data);
      } catch (error) {
        console.error('Failed to fetch trending', error);
      }
    };
    fetchTrending();
  }, []);

  useEffect(() => {
    // Weekly top products (last 7 days) for the selected city
    const fetchTopProducts = async () => {
      if (!selectedCity) {
        setTopProducts([]);
        return;
      }
      try {
        const params = new URLSearchParams({
          limit: '5',
          city: selectedCity.id,
        });
        const response = await fetch(`${API}/insights/top-products-week?${params.toString()}`);
        const data = await response.json();
        setTopProducts(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to fetch top products', error);
      }
    };
    fetchTopProducts();
  }, [selectedCity]);

  useEffect(() => {
    const fetchOpenRequests = async () => {
      if (!selectedCity) {
        setOpenRequests([]);
        return;
      }
      setRequestsLoading(true);
      try {
        const params = new URLSearchParams({
          city: selectedCity.id,
          limit: '10',
        });
        const response = await fetch(`${API}/requests?${params.toString()}`);
        const data = await response.json();
        setOpenRequests(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to fetch requests', error);
      } finally {
        setRequestsLoading(false);
      }
    };
    fetchOpenRequests();
  }, [selectedCity]);

  useEffect(() => {
    const fetchMyRequests = async () => {
      if (!token) {
        setMyRequests([]);
        return;
      }
      try {
        const response = await fetch(`${API}/requests/mine?limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        setMyRequests(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to fetch my requests', error);
      }
    };
    fetchMyRequests();
  }, [token]);

  const getUnseenFulfilled = () => {
    if (!token) return [];
    const seenKey = 'gw_seen_fulfilled_request_ids';
    const seenRaw = localStorage.getItem(seenKey) || '[]';
    let seen = [];
    try {
      seen = JSON.parse(seenRaw);
      if (!Array.isArray(seen)) seen = [];
    } catch {
      seen = [];
    }
    return (myRequests || [])
      .filter((r) => (r?.status || 'open') === 'fulfilled')
      .filter((r) => r?.id && !seen.includes(r.id));
  };

  const markFulfilledAsSeen = (ids) => {
    const seenKey = 'gw_seen_fulfilled_request_ids';
    const seenRaw = localStorage.getItem(seenKey) || '[]';
    let seen = [];
    try {
      seen = JSON.parse(seenRaw);
      if (!Array.isArray(seen)) seen = [];
    } catch {
      seen = [];
    }
    const nextSeen = Array.from(new Set([...(seen || []), ...(ids || [])]));
    localStorage.setItem(seenKey, JSON.stringify(nextSeen));
  };

  const createRequest = async () => {
    if (!token) {
      navigate('/auth');
      return;
    }
    const product_name = requestProduct.trim();
    const details = requestDetails.trim();
    if (!product_name) {
      toast.error('Please enter a product name');
      return;
    }
    if (!selectedCity) return;

    setCreatingRequest(true);
    try {
      const response = await fetch(`${API}/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          product_name,
          details: details || null,
          city: selectedCity.id,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Could not create request');
        return;
      }
      toast.success('Request posted! The community will help.');
      setRequestProduct('');
      setRequestDetails('');
      setShowRequestForm(false);
      if (data?.request) {
        setOpenRequests((prev) => [data.request, ...(prev || [])].slice(0, 10));
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setCreatingRequest(false);
    }
  };

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const response = await fetch(`${API}/products/autocomplete?q=${query}`);
        const data = await response.json();
        setSuggestions(data);
      } catch (error) {
        console.error('Failed to fetch suggestions', error);
      }
    };

    if (query.length >= 2) {
      fetchSuggestions();
      setShowTrending(false);
    } else {
      setSuggestions([]);
    }
  }, [query]);

  const trackSearch = async (searchQuery) => {
    try {
      await fetch(`${API}/track-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product: searchQuery }),
      });
    } catch (error) {
      console.error('Failed to track search', error);
    }
  };

  const handleSearch = (searchQuery = query) => {
    if (searchQuery.trim()) {
      trackSearch(searchQuery);
      navigate(`/results?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleTrendingClick = (trendingItem) => {
    setQuery(trendingItem);
    setShowTrending(false);
    handleSearch(trendingItem);
  };

  const handleChangeCity = () => {
    selectCity(null);
    navigate('/');
  };

  const formatLastReported = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffHours < 24) return `${Math.max(diffHours, 1)}h ago`;
    return `${diffDays}d ago`;
  };

  const formatRequestAge = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMin < 60) return `${Math.max(diffMin, 1)}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const weeklyReportCount = (topProducts || []).reduce(
    (sum, item) => sum + (Number(item?.report_count) || 0),
    0
  );

  const weeklyCarouselRef = useRef(null);
  const pauseUntilRef = useRef(0);

  const onWeeklyCarouselInteract = () => {
    // Pause autoplay for a short moment after user interaction.
    pauseUntilRef.current = Date.now() + 10000;
  };

  const advanceWeeklyCarousel = () => {
    const el = weeklyCarouselRef.current;
    if (!el) return;

    const buttons = el.querySelectorAll('button');
    if (!buttons || buttons.length === 0) return;

    const step = Math.round(buttons[0].getBoundingClientRect().width + 12);
    const nextLeft = el.scrollLeft + step;

    // If we're near the end, wrap to start.
    if (nextLeft + el.getBoundingClientRect().width >= el.scrollWidth) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      el.scrollTo({ left: nextLeft, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (!topProducts || topProducts.length <= 1) return;

    const intervalMs = 3000;
    const timer = window.setInterval(() => {
      if (Date.now() < pauseUntilRef.current) return;
      advanceWeeklyCarousel();
    }, intervalMs);

    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topProducts.length]);

  return (
    <div className="min-h-screen bg-background pb-40 sm:pb-32">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-primary">Vexi</h1>
            <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-muted-foreground">
              <span className="max-w-[140px] sm:max-w-none truncate">
                📍 {selectedCity?.name}
              </span>
              <button
                data-testid="change-city-button"
                onClick={handleChangeCity}
                className="text-accent hover:underline ml-1 sm:ml-2 whitespace-nowrap"
              >
                Change
              </button>
            </div>
          </div>
          <div className="relative flex items-center gap-2">
            <Button
              data-testid="theme-toggle-button"
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            {token && (
              <Button
                data-testid="notifications-button"
                variant="ghost"
                size="icon"
                onClick={() => {
                  const next = !notifOpen;
                  setNotifOpen(next);
                  if (next) {
                    const unseen = getUnseenFulfilled();
                    if (unseen.length > 0) markFulfilledAsSeen(unseen.map((r) => r.id));
                  }
                }}
                className="relative"
              >
                <Bell className="w-5 h-5" />
                {getUnseenFulfilled().length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] flex items-center justify-center">
                    {Math.min(getUnseenFulfilled().length, 9)}
                  </span>
                )}
              </Button>
            )}
            {token ? (
              <Button
                data-testid="profile-button"
                variant="ghost"
                size="icon"
                onClick={() => navigate('/profile')}
              >
                <User className="w-5 h-5" />
              </Button>
            ) : (
              <Button
                data-testid="auth-button-search"
                onClick={() => navigate('/auth')}
                variant="outline"
                size="sm"
              >
                Sign In
              </Button>
            )}

            {token && notifOpen && (
              <div className="absolute right-0 top-[52px] w-[340px] max-w-[calc(100vw-2rem)] z-50">
                <Card className="p-3 shadow-xl">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-foreground">Updates</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                      onClick={() => setNotifOpen(false)}
                    >
                      Close
                    </Button>
                  </div>

                  <div className="mt-3">
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                      We found what you were looking for
                    </div>
                    {getUnseenFulfilled().length === 0 ? (
                      <div className="text-sm text-muted-foreground">No new matches yet.</div>
                    ) : (
                      <div className="space-y-2">
                        {getUnseenFulfilled().slice(0, 5).map((r) => (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => {
                              setNotifOpen(false);
                              navigate(`/results?q=${encodeURIComponent(r.product_name || '')}`);
                            }}
                            className="w-full text-left rounded-lg border border-border bg-muted/30 px-3 py-2 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-medium text-foreground line-clamp-1">
                                {r.product_name}
                              </div>
                              <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                            </div>
                            {r.details && (
                              <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                                {r.details}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                      People are looking for
                    </div>
                    {requestsLoading ? (
                      <div className="text-sm text-muted-foreground">Loading…</div>
                    ) : openRequests.length === 0 ? (
                      <div className="text-sm text-muted-foreground">No open requests right now.</div>
                    ) : (
                      <div className="space-y-2">
                        {openRequests.slice(0, 5).map((r) => (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => {
                              setNotifOpen(false);
                              setQuery(r.product_name || '');
                              handleSearch(r.product_name || '');
                            }}
                            className="w-full text-left rounded-lg border border-border bg-background px-3 py-2 hover:bg-muted/40 transition-colors"
                          >
                            <div className="text-sm font-medium text-foreground line-clamp-1">
                              {r.product_name}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatRequestAge(r.created_at)}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-heading font-bold text-foreground mb-4 tracking-tight">
            Find the best prices near you
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground">
            Real prices from real shoppers in {selectedCity?.name}
          </p>
        </div>

        <div className="mb-8">
          <div className="relative">
            <SearchIcon className="absolute left-4 top-4 w-5 h-5 text-muted-foreground" />
            <Input
              data-testid="product-search-input"
              type="text"
              placeholder="Search for a product (e.g., milk, bread, rice)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => {
                if (!query) setShowTrending(true);
              }}
              onBlur={() => {
                setTimeout(() => setShowTrending(false), 200);
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="h-12 rounded-full border-2 border-primary/20 focus:border-primary focus:ring-4 focus:ring-primary/10 bg-background pl-12 pr-4 text-base shadow-sm"
            />
            {!query && (
              <button
                onClick={() => setShowChat(true)}
                className="absolute right-4 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors font-medium group"
                data-testid="search-ask-bot-button"
              >
                <Bot className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">Ask Vexi</span>
              </button>
            )}
          </div>
          {!query && (
            <div className="mt-2 sm:hidden flex justify-end">
              <button
                type="button"
                onClick={() => setShowChat(true)}
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                data-testid="search-ask-bot-button-mobile"
              >
                <Bot className="w-4 h-4" />
                Ask Vexi
              </button>
            </div>
          )}

          <p className="mt-3 text-sm text-muted-foreground text-center">
            Based on{' '}
            <span className="font-semibold text-foreground">
              {weeklyReportCount > 0 ? `${weeklyReportCount}+` : '100+'}
            </span>{' '}
            price reports from shoppers near you
          </p>

          {/* Trending Items - Show on focus when no query */}
          {showTrending && trending.length > 0 && (
            <div className="mt-3 bg-card border border-border rounded-2xl shadow-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-accent" />
                <span className="text-sm font-medium text-foreground">Trending Searches</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {trending.map((item, idx) => (
                  <Badge
                    key={idx}
                    data-testid={`trending-item-${idx}`}
                    onClick={() => handleTrendingClick(item.name)}
                    className="cursor-pointer hover:bg-primary/90 transition-colors rounded-full px-4 py-2 bg-primary text-primary-foreground"
                  >
                    {item.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Autocomplete Suggestions */}
          {suggestions.length > 0 && query.length >= 2 && (
            <div className="mt-3 bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
              {suggestions.map((item, idx) => (
                <button
                  key={idx}
                  data-testid={`suggestion-item-${idx}`}
                  onClick={() => {
                    setQuery(item.name);
                    handleSearch(item.name);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-muted transition-colors duration-200 border-b border-border last:border-b-0"
                >
                  <span className="text-foreground">{item.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-center gap-4">
          <Button
            data-testid="search-button"
            onClick={() => handleSearch()}
            size="lg"
            className="rounded-full px-8 bg-primary hover:bg-primary/90"
          >
            <SearchIcon className="w-5 h-5 mr-2" />
            Search Prices
          </Button>
          {token && (
            <Button
              data-testid="report-price-button"
              onClick={() => navigate('/report')}
              variant="outline"
              className="rounded-full"
            >
              <Plus className="w-5 h-5 mr-2" />
              Report a Price
            </Button>
          )}
        </div>

        {topProducts.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-accent" />
              <span className="text-sm font-medium text-foreground">
                This week&apos;s most reported products
              </span>
            </div>
            <div
              ref={weeklyCarouselRef}
              className="weekly-carousel flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory"
              onTouchStart={onWeeklyCarouselInteract}
              onMouseEnter={onWeeklyCarouselInteract}
              onScroll={onWeeklyCarouselInteract}
            >
              {topProducts.map((item, idx) => (
                <button
                  key={`${item.product_name}-${idx}`}
                  onClick={() => {
                    const term = item.product_name || '';
                    if (!term) return;
                    setQuery(term);
                    handleSearch(term);
                  }}
                  className="min-w-[230px] sm:min-w-[260px] snap-start rounded-2xl border border-border bg-card p-3 text-left hover:shadow-md transition-shadow"
                >
                  <div className="text-sm font-medium text-foreground line-clamp-2">
                    {item.product_name}
                    {item.brand && <span className="text-muted-foreground"> • {item.brand}</span>}
                    {item.size && <span className="text-muted-foreground"> ({item.size})</span>}
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{item.report_count} report{item.report_count === 1 ? '' : 's'}</span>
                    <span className="text-primary font-semibold">avg R{item.avg_price.toFixed(2)}</span>
                  </div>
                  {item.has_combo && (item.deal_label || item.deal_quantity || item.deal_price) && (
                    <div className="mt-2">
                      <Badge variant="secondary" className="text-[11px]">
                        Deal{item.deal_label ? `: ${item.deal_label}` : ''}
                      </Badge>
                    </div>
                  )}
                  {item.last_reported && (
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      Updated {formatLastReported(item.last_reported)}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-accent" />
              <span className="text-sm font-medium text-foreground">
                People are looking for
              </span>
            </div>
            <Button
              size="sm"
              variant={showRequestForm ? 'default' : 'outline'}
              className="rounded-full"
              onClick={() => setShowRequestForm((v) => !v)}
            >
              Can&apos;t find it? Request it
            </Button>
          </div>

          {showRequestForm && (
            <Card className="p-4 mb-3">
              <div className="grid gap-3">
                <Input
                  placeholder="Product name (e.g., Weet-Bix)"
                  value={requestProduct}
                  onChange={(e) => setRequestProduct(e.target.value)}
                />
                <textarea
                  placeholder="Details (optional): brand, size, price target..."
                  value={requestDetails}
                  onChange={(e) => setRequestDetails(e.target.value)}
                  className="min-h-[80px] rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    className="rounded-full"
                    onClick={() => setShowRequestForm(false)}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="rounded-full bg-primary hover:bg-primary/90"
                    onClick={createRequest}
                    disabled={creatingRequest}
                    type="button"
                  >
                    {creatingRequest ? 'Posting...' : 'Post request'}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {requestsLoading ? (
            <p className="text-sm text-muted-foreground">Loading requests...</p>
          ) : openRequests.length === 0 ? (
            <Card className="p-4 text-sm text-muted-foreground">
              No open requests yet in {selectedCity?.name}. Be the first to request something!
            </Card>
          ) : (
            <div className="space-y-2">
              {openRequests.map((r) => (
                <Card key={r.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-foreground">
                        {r.product_name}
                      </div>
                      {r.details && (
                        <div className="text-sm text-muted-foreground mt-1">
                          {r.details}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatRequestAge(r.created_at)}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => {
                        const term = r.product_name || '';
                        if (!term) return;
                        setQuery(term);
                        handleSearch(term);
                      }}
                    >
                      Search
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Floating actions (Explore + Ask Vexi) */}
      <div
        className="fixed inset-x-0 z-50 pointer-events-none transition-[bottom] duration-100 ease-out"
        style={{ bottom: `${footerLiftPx}px` }}
      >
        <div className="grid grid-cols-3 items-end px-6 pointer-events-none">
          {/* Explore: left on mobile, centered on desktop */}
          <div
            className={[
              'col-start-1 justify-self-start sm:col-start-2 sm:justify-self-center pointer-events-auto',
              showChat ? 'invisible sm:visible' : 'visible',
            ].join(' ')}
          >
            <Button
              data-testid="explore-fab-button"
              onClick={() => navigate('/explore')}
              className={[
                'rounded-full shadow-lg hover:shadow-xl transition-shadow duration-300 bg-primary hover:bg-primary/90',
                'px-0 w-14 h-14 sm:px-5 sm:w-auto sm:h-14',
                'inline-flex',
              ].join(' ')}
            >
              <MapIcon className="w-5 h-5 sm:mr-2" />
              <span className="hidden sm:inline">Explore nearby deals</span>
            </Button>
          </div>

          {/* Ask Vexi: right */}
          <div className="col-start-3 justify-self-end pointer-events-auto">
            <Button
              data-testid="chat-toggle-button"
              onClick={() => setShowChat(!showChat)}
              className="rounded-full w-14 h-14 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-primary hover:bg-primary/90"
              size="icon"
            >
              {showChat ? <X className="w-6 h-6" /> : <Bot className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Floating Chat Window */}
      {showChat && (
        <Suspense
          fallback={
            <div
              style={{ bottom: `${footerLiftPx + 80}px` }}
              className="fixed right-6 w-96 max-w-[calc(100vw-3rem)] bg-card border border-border rounded-3xl shadow-2xl z-50 p-4 text-sm text-muted-foreground"
            >
              Loading Ask Vexi…
            </div>
          }
        >
          <AskVexiChat
            apiBase={API}
            cityId={selectedCity?.id}
            userLocation={userLocation}
            bottomPx={footerLiftPx + 80}
            onClose={() => setShowChat(false)}
          />
        </Suspense>
      )}
    </div>
  );
}
